import React from 'react';
import { ExternalLink, Code2 } from 'lucide-react';

const Home = ({ projects, experiences }) => {
  const textSecondary = 'text-gray-600 dark:text-gray-400';
  const textColor = 'text-gray-900 dark:text-gray-100';

  return (
    <div className="max-w-2xl mx-auto px-6 py-10 pb-20">
      {/* Hero Section */}
      <section id="home" className="py-12">
        <h1 className="text-5xl md:text-6xl font-bold mb-4">
          Victor Mwangi
        </h1>
        <p className={`text-lg ${textSecondary} mb-8`}>
          Software Engineer • Backend Developer • Security Analyst
        </p>
      </section>

      {/* About Section */}
      <section id="about" className="py-8">
        <h2 className="text-2xl font-bold mb-6">About</h2>
        <div className={`space-y-4 ${textSecondary} leading-relaxed`}>
          <p>
            I work at the intersection of technology and real-world problem solving, turning complex
            ideas into clear, practical outcomes. My approach blends analytical precision with a
            builder's instinct—creating systems that are both performant and meaningful.
          </p>
          <p>
            Based in Nairobi, Kenya, I've spent 5+ years building backend systems that scale across
            diverse ecosystems. From enterprise e-commerce platforms to data pipelines processing
            high-volume streams, I care about making work that feels lightweight, adaptable, and
            built to last.
          </p>
          <p>
            During my time at DigitalQatalyst, I developed headless commerce solutions with Vendure, architectured
            Azure integrations, and designed GraphQL APIs that power seamless user experiences.
          </p>
		 <p>
			When I am not stuck on my computer coding, I am probably hiking, cycling or skating.
		 </p>
        </div>
      </section>

      {/* Projects */}
      <section id="projects" className="py-8">
        <h2 className="text-2xl font-bold mb-8">Projects</h2>
        <div className="space-y-8">
          {projects.map((project, idx) => (
            <div key={idx} className="space-y-2">
              <div className="flex items-start justify-between gap-4">
                <h3 className="text-lg font-medium">{project.title}</h3>
                <div className="flex gap-3 shrink-0">
                  {project.link && (
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${textSecondary} hover:${textColor} transition-colors`}
                      aria-label="Live demo"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  {project.github && (
                    <a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${textSecondary} hover:${textColor} transition-colors`}
                      aria-label="GitHub"
                    >
                      <Code2 className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
              <p className={`${textSecondary} text-sm leading-relaxed`}>
                {project.description}
              </p>
              <div className="flex flex-wrap gap-2">
                {project.tech.map((tech) => (
                  <span
                    key={tech}
                    className={`text-xs ${textSecondary}`}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Experience */}
      <section id="experience" className="py-8">
        <h2 className="text-2xl font-bold mb-8">Experience</h2>
        <div className="space-y-8">
          {experiences.map((exp, idx) => (
            <div key={idx} className="space-y-2">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-medium">{exp.role}</h3>
                  <p className={`${textSecondary} text-sm`}>{exp.company}</p>
                </div>
                <span className={`${textSecondary} text-sm shrink-0`}>{exp.period}</span>
              </div>
              <p className={`${textSecondary} text-sm leading-relaxed`}>
                {exp.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="py-8">
        <h2 className="text-2xl font-bold mb-6">Let's connect</h2>
        <div className="flex flex-wrap gap-4">
          <a
            href="mailto:mwangiruoya@gmail.com"
            className={`${textSecondary} hover:${textColor} transition-colors text-sm`}
          >
            Email
          </a>
          <a
            href="https://github.com/ru0ya"
            target="_blank"
            rel="noopener noreferrer"
            className={`${textSecondary} hover:${textColor} transition-colors text-sm`}
          >
            GitHub
          </a>
          <a
            href="https://www.linkedin.com/in/victor-mwangi-ruoya/"
            target="_blank"
            rel="noopener noreferrer"
            className={`${textSecondary} hover:${textColor} transition-colors text-sm`}
          >
            LinkedIn
          </a>
          <a
            href="tel:+254715067724"
            className={`${textSecondary} hover:${textColor} transition-colors text-sm`}
          >
            +254 715 067 724
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
